import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Question from '../../common/Question';
import RateInput from '../../common/RateInput';
import TextAreaInput from '../../common/TextAreaInput';
import YesNoInput from '../../common/YesNoInput';
import YesNoInputGroup from '../../common/YesNoInputGroup';

const createOfficeQuestion = (workLocation, officeLocation, officeDays, daysInOffice) => {
  if (!['office', 'both', 'hybrid'].includes(workLocation)) {
    return null;
  }

  const locationClause = officeLocation ? ` located in ${officeLocation}` : '';
  let daysClause = '';
  if ((officeDays && officeDays !== 'not sure') || daysInOffice) {
    const dayWord = daysInOffice === '1' ? 'day' : 'days';
    daysClause = ` for at least ${daysInOffice} ${dayWord} per week`;
  }
  return `Can you work out of the office${locationClause}${daysClause}?`;
};

const error = (errors, input, typeOfError) => (
  errors && errors[input] === typeOfError
);

function LogisticsTab({
  project,
  proposal,
  onFormChange,
  errors,
  nonCompeteUrl,
  showBackgroundCheck,
}) {
  const { project_travel_locations, languages } = project;

  const currentYear = new Date().getFullYear();
  const startDateYear = project.approximate_start_date_local
    ? new Date(project.approximate_start_date_local).getFullYear() : 0;
  const dateFormat = startDateYear === currentYear ? 'MMMM Do' : 'MMMM Do, YYYY';

  const travelLocations = project_travel_locations;
  const travelLocationDisplay = [
    travelLocations.slice(0, -1).join(', '),
    travelLocations.slice(-1)[0],
  ].join(travelLocations.length < 2 ? '' : ' and ');

  const timeClauses = [];
  if (project.avg_time_required_range && project.avg_time_required_range !== 'not sure') {
    timeClauses.push(`${project.avg_time_required_range} hours per week`);
  }

  if (project.duration_range && project.duration_range !== 'not sure') {
    timeClauses.push(`for ${project.duration_range} month duration`);
  }

  if (project.approximate_start_date_local) {
    const startDate = window.moment(project.approximate_start_date_local).format(dateFormat);
    const past = window.moment(Date()) > window.moment(project.approximate_start_date_local);
    timeClauses.push(past ? 'ASAP' : `starting on ${startDate}`);
  }
  const availabilityQuestion = project.employment_type === 'consulting'
    ? `Are you available ${timeClauses.join(' ')}?` : null;

  const officeQuestion = createOfficeQuestion(
    project.work_location,
    project.office_location,
    project.office_days,
    project.days_in_office_from_office_days,
  );

  const allTrue = (key) => proposal[key] && proposal[key].every((item) => item.selected === 'Yes');
  const isDescEmpty = (desc) => !desc || !desc.trim();

  let availabilityError = null;
  if (isDescEmpty(proposal.availability_desc)) {
    availabilityError = errors.availability_desc;
  }

  let workInOfficeError = null;
  if (isDescEmpty(proposal.work_in_office_desc)) {
    workInOfficeError = errors.work_in_office_desc;
  }

  let travelLocationError = null;
  if (isDescEmpty(proposal.travel_locations_desc)) {
    travelLocationError = errors.travel_locations_desc;
  }

  let taxFormError = null;
  if (isDescEmpty(proposal.tax_form_desc)) {
    taxFormError = errors.tax_form_desc;
  }

  let agreementError = null;
  if (isDescEmpty(proposal.agreement_desc)) {
    agreementError = errors.agreement_desc;
  }

  let nonCompeteError = null;
  if (isDescEmpty(proposal.non_compete_desc)) {
    nonCompeteError = errors.non_compete_desc;
  }

  const languagesAllTrue = allTrue('languages');
  let languagesError = null;
  if (!languagesAllTrue && isDescEmpty(proposal.languages_desc)) {
    languagesError = errors.languages_desc && errors.languages_desc[0];
  }

  const numberIndexes = [1, 2, 3, 4, 5, 6, 7, 8, 9];

  return (
    <div className="mb-4 logistics-tab">
      { availabilityQuestion && project.employment_type === 'consulting'
        && (
        <div key="availability-inputs">
          <Question
            title={`${numberIndexes.shift()}. ${availabilityQuestion}`}
            error={error(errors, 'availability', 'radio_button')}
            location="proposal-edit"
            className="mb-5"
          >
            <YesNoInput
              id="availability"
              className="ms-4 mt-4"
              showDescriptionInput
              onAnswerChange={(val) => onFormChange('availability', val)}
              onDescAnswerChange={(val) => onFormChange('availability_desc', val)}
              errors={errors}
              value={proposal.availability}
              descValue={proposal.availability_desc}
              textPromptClass={availabilityError && 'with-error-text'}
              textAreaPrompt="Tell us more about your availability for this project."
            />
          </Question>
        </div>
        )}

      { officeQuestion
        && (
        <div key="work-in-office-inputs">
          <Question
            title={`${numberIndexes.shift()}. ${officeQuestion}`}
            error={error(errors, 'work_in_office', 'radio_button')}
            location="proposal-edit"
            className="mb-5"
          >
            <YesNoInput
              id="work_in_office"
              className="ms-4 mt-4"
              showDescriptionInput
              onAnswerChange={(val) => onFormChange('work_in_office', val)}
              onDescAnswerChange={(val) => onFormChange('work_in_office_desc', val)}
              errors={errors}
              value={proposal.work_in_office}
              descValue={proposal.work_in_office_desc}
              textPromptClass={workInOfficeError && 'with-error-text'}
              textAreaPrompt="Tell us more about your location for this project."
            />
          </Question>
        </div>
        )}

      { project_travel_locations.length > 0
        && (
        <div key="travel-locations-inputs">
          <Question
            title={`${numberIndexes.shift()}. Are you able to meet the Travel Requirements: ${travelLocationDisplay}?`}
            error={error(errors, 'travel_locations', 'radio_button')}
            location="proposal-edit"
            className="mb-5"
          >
            <YesNoInput
              id="travel_locations"
              className="ms-4 mt-4"
              showDescriptionInput
              onAnswerChange={(val) => onFormChange('travel_locations', val)}
              onDescAnswerChange={(val) => onFormChange('travel_locations_desc', val)}
              errors={errors}
              value={proposal.travel_locations}
              descValue={proposal.travel_locations_desc}
              textPromptClass={travelLocationError && 'with-error-text'}
              textAreaPrompt="Tell us more about your traveling availability."
            />
          </Question>
        </div>
        )}

      { languages.length > 0
        && (
        <div>
          <Question
            className="qualifications-question mb-5"
            title={`${numberIndexes.shift()}. Are you fluent in these languages?`}
            location="proposal-edit"
          >
            <div className="ms-4">
              <YesNoInputGroup
                options={languages}
                formKey="languages"
                value={proposal.languages}
                onAnswerChange={(val) => onFormChange('languages', val)}
                errors={errors}
              />
            </div>

            {errors && errors.languages_desc
              && (
              <div className="body-text-2 medium-weight with-error-text mb-2">
                <i className="fa fa-exclamation-triangle" />
                {' '}
                Required!
              </div>
              )}
            { proposal.languages && proposal.languages.some((l) => l.selected !== null)
              && (
              <div className="ms-4 input-container">
                <TextAreaInput
                  className="proposal-text-area language-text-area"
                  placeholder="Tell us more..."
                  onAnswerChange={(val) => onFormChange('languages_desc', val)}
                  error={languagesError}
                  textAreaPrompt="Tell us more about that. (NOT required)"
                  textPromptClass={languagesError && 'with-error-text'}
                  value={proposal.languages_desc}
                />
              </div>
              )}
          </Question>
        </div>
        )}

      { project.employment_type === 'consulting' && project.tax_type
          && project.tax_type !== 'n/a'
          && (
          <div key="tax-form-inputs">
            <Question
              title={`${numberIndexes.shift()}. This is a ${project.tax_type} role. Is that ok?`}
              error={error(errors, 'tax_form', 'radio_button')}
              location="proposal-edit"
              className="mb-5"
            >
              <YesNoInput
                id="tax_form"
                className="ms-4 mt-4"
                showDescriptionInput
                onAnswerChange={(val) => onFormChange('tax_form', val)}
                onDescAnswerChange={(val) => onFormChange('tax_form_desc', val)}
                errors={errors}
                value={proposal.tax_form}
                descValue={proposal.tax_form_desc}
                textPromptClass={taxFormError && 'with-error-text'}
              />
            </Question>
          </div>
          )}

      <div key="agreement-inputs">
        <Question
          title={`${numberIndexes.shift()}. Are you already under a non-compete agreement with ANOTHER employer
  that would prevent you from working on this project?`}
          error={error(errors, 'agreement', 'radio_button')}
          location="proposal-edit"
          className="mb-5"
        >
          <YesNoInput
            id="agreement"
            className="ms-4 mt-4"
            showDescriptionInput
            onAnswerChange={(val) => onFormChange('agreement', val)}
            onDescAnswerChange={(val) => onFormChange('agreement_desc', val)}
            errors={errors}
            value={proposal.agreement}
            descValue={proposal.agreement_desc}
            reverseAnswer
            textPromptClass={agreementError && 'with-error-text'}
          />
        </Question>
      </div>

      { project.consultant_needs_sign_agreement
        && (
        <div>
          <Question
            title={`${numberIndexes.shift()}. Are you willing to sign a non-compete agreement for this project?`}
            error={error(errors, 'non_compete', 'radio_button')}
            location="proposal-edit"
            className="mb-5"
          >
            <div>
              { nonCompeteUrl
                && (
                <a
                  className="non-compete-link"
                  href={nonCompeteUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  View non-compete document
                </a>
                )}
              <div>
                <YesNoInput
                  id="non_compete"
                  className="mt-3 ms-4"
                  showDescriptionInput
                  onAnswerChange={(val) => onFormChange('non_compete', val)}
                  onDescAnswerChange={(val) => onFormChange('non_compete_desc', val)}
                  errors={errors}
                  value={proposal.non_compete}
                  descValue={proposal.non_compete_desc}
                  textPromptClass={nonCompeteError && 'with-error-text'}
                />
              </div>
            </div>
          </Question>
        </div>
        )}

      {errors && errors.hourly_rate
      && (
      <div>
        <div className="body-text-2 medium-weight with-error-text mb-2">
          <i className="fa fa-exclamation-triangle" />
          {' '}
          Required!
        </div>
        <div className="body-text-2 medium-weight with-error-text mb-2">
          Please enter a number as an hourly rate.
        </div>
      </div>
      )}
      { project.employment_type === 'consulting'
        && (
        <Question
          title={`${numberIndexes.shift()}. What is your hourly rate?`}
          location="proposal-edit"
          className="mb-5"
        >
          <div className="mt-3 ms-4">
            <RateInput
              value={proposal.hourly_rate}
              onAnswerChange={(val) => onFormChange('hourly_rate', val)}
              placeholder="Enter rate"
              decimalsAllowed={false}
            />
          </div>
        </Question>
        )}
      { project.employment_type === 'full_time'
        && (
        <Question
          title={`${numberIndexes.shift()}. Will you now or in the future require sponsorship to work within the United States?`}
          location="proposal-edit"
          className="mb-5"
        >
          <YesNoInput
            id="us_sponsorship_needed"
            className="ms-4 mt-4"
            showDescriptionInput
            onAnswerChange={(val) => onFormChange('us_sponsorship_needed', val)}
            onDescAnswerChange={(val) => onFormChange('us_sponsorship_needed_desc', val)}
            reverseAnswer
            errors={errors}
            value={proposal.us_sponsorship_needed}
            descValue={proposal.us_sponsorship_needed_desc}
            textPromptClass={`us_sponsorship_needed-text-area ${nonCompeteError && 'with-error-text'}`}
          />
        </Question>
        )}

      {(showBackgroundCheck && project.background_check_type.length)
        && (
        <Question
          title={`${numberIndexes.shift()}. Are you willing to undergo checks/provide documents as listed?`}
          location="proposal-edit"
          className="mb-5"
        >
          <Fragment>
            <ol>
              {project.background_check_type.map((v) => (
                <li className="mb-3">{v}</li>
              ))}
            </ol>
            <YesNoInput
              id="background_check_type"
              className="ms-4 mt-4"
              showDescriptionInput
              onAnswerChange={(val) => onFormChange('background_check_type', val)}
              onDescAnswerChange={(val) => onFormChange('background_check_type_desc', val)}
              errors={errors}
              value={proposal.background_check_type}
              descValue={proposal.background_check_type_desc}
              textPromptClass={travelLocationError && 'with-error-text'}
              textAreaPrompt="Tell us why you're not willing to undergo the checks or provide the documents listed."
            />
          </Fragment>
        </Question>
        )}

      { project.compensation_benefit_note
        && (
        <Question
          title={`${numberIndexes.shift()}. Compensation & Benefits.`}
          location="proposal-edit"
          className="mb-5"
        >
          <div className="ms-4">{project.compensation_benefit_note}</div>
        </Question>
        )}
    </div>
  );
}

LogisticsTab.propTypes = {
  project: PropTypes.shape({}).isRequired,
  proposal: PropTypes.shape({}).isRequired,
  onFormChange: PropTypes.func.isRequired,
  errors: PropTypes.shape({}).isRequired,
  nonCompeteUrl: PropTypes.string,
  showBackgroundCheck: PropTypes.bool,
};

LogisticsTab.defaultProps = {
  nonCompeteUrl: null,
  showBackgroundCheck: false,
};

export default LogisticsTab;
